import { parseMagentoDate } from './datetime';
import { getCategoryInfo } from './itemSort';

export const SLA48_HOURS = 48;
export const SLA72_HOURS = 72;
export const RISK48_HOURS = 40;
export const RISK72_HOURS = 64;

const normalizeText = (value) => String(value || '').trim();
const normalizeKey = (value) => normalizeText(value).toLowerCase();

const getHistoryTime = (entry) => {
  const parsed = parseMagentoDate(entry?.created_at || entry?.createdAt || '');
  return parsed ? parsed.getTime() : 0;
};

export const getSortedStatusHistories = (histories = []) => {
  if (!Array.isArray(histories)) return [];
  return [...histories].sort((a, b) => getHistoryTime(a) - getHistoryTime(b));
};

export const parseAssignmentComment = (comment = '') => {
  const match = String(comment).match(/Assigned to\s([^—]+)\s—/);
  return match?.[1] ? match[1].trim() : '';
};

export const parseReleaseComment = (comment = '') => {
  const match = String(comment).match(/Released back to processing by\s(.+)$/i);
  return match?.[1] ? match[1].trim() : '';
};

export const getOrderAssignment = (order = {}) => {
  const histories = getSortedStatusHistories(order.status_histories || []);
  let latestAssigned = null;
  let latestReleased = null;

  histories.forEach((entry) => {
    const comment = entry?.comment || '';
    const at = parseMagentoDate(entry?.created_at || entry?.createdAt || '');
    const assignedTo = parseAssignmentComment(comment);
    if (assignedTo) {
      latestAssigned = {
        operator: assignedTo,
        at,
        comment,
      };
    }

    const releasedBy = parseReleaseComment(comment);
    if (releasedBy) {
      latestReleased = {
        operator: releasedBy,
        at,
        comment,
      };
    }
  });

  const assignedAtTime = latestAssigned?.at?.getTime() || 0;
  const releasedAtTime = latestReleased?.at?.getTime() || 0;
  const status = normalizeKey(order.status);
  const isReleasedAfterAssignment = releasedAtTime >= assignedAtTime && releasedAtTime > 0;
  const currentOperator = status === 'processing' || isReleasedAfterAssignment
    ? ''
    : latestAssigned?.operator || '';

  return {
    currentOperator,
    assignedAt: currentOperator ? latestAssigned?.at || null : null,
    releasedAt: latestReleased?.at || null,
    releasedBy: latestReleased?.operator || '',
    latestAssignedAt: latestAssigned?.at || null,
    latestAssignedTo: latestAssigned?.operator || '',
    isReleasedAfterAssignment,
  };
};

export const getFriendlyShipping = (description) => {
  const raw = description || '';
  const shipping = raw.toLowerCase();
  if (shipping.includes('pickup')) return 'Click & Collect';
  if (shipping.includes('dublin')) return 'Xpect';
  if (shipping.includes('dpd')) return 'Next-day';
  return raw || 'N/A';
};

export const getFirstStatusAt = (order, targetStatus) => {
  const target = normalizeKey(targetStatus);
  const events = getSortedStatusHistories(order?.status_histories || [])
    .map((entry) => {
      const status = normalizeKey(entry?.status);
      const at = parseMagentoDate(entry?.created_at || entry?.createdAt || '');
      return { status, at };
    })
    .filter((entry) => entry.at && entry.status);

  const hit = events.find((entry) => entry.status === target);
  return hit?.at || null;
};

export const computeAgeHours = (createdAt, now = new Date()) => {
  const parsed = parseMagentoDate(createdAt);
  if (!parsed) return null;
  const diffMs = now.getTime() - parsed.getTime();
  if (!Number.isFinite(diffMs)) return null;
  return diffMs / (1000 * 60 * 60);
};

export const calcSla = (order, now = new Date(), pickingStatus = 'picking') => {
  const createdAt = parseMagentoDate(order?.created_at);
  const pickingAt = getFirstStatusAt(order, pickingStatus);
  const completeAt = getFirstStatusAt(order, 'complete');
  const nowDate = now || new Date();

  const hToPicking = createdAt ? (pickingAt || nowDate).getTime() - createdAt.getTime() : null;
  const hToComplete = createdAt ? (completeAt || nowDate).getTime() - createdAt.getTime() : null;

  const hoursToPicking = hToPicking === null ? null : hToPicking / (1000 * 60 * 60);
  const hoursToComplete = hToComplete === null ? null : hToComplete / (1000 * 60 * 60);

  return {
    createdAt,
    pickingAt,
    completeAt,
    hoursToPicking,
    hoursToComplete,
    sla48Compliant: hoursToPicking !== null && !!pickingAt && hoursToPicking <= SLA48_HOURS,
    sla72Compliant: hoursToComplete !== null && !!completeAt && hoursToComplete <= SLA72_HOURS,
    sla48Breached: hoursToPicking !== null && !pickingAt && hoursToPicking >= SLA48_HOURS,
    sla72Breached: hoursToComplete !== null && !completeAt && hoursToComplete >= SLA72_HOURS,
    risk48: hoursToPicking !== null && !pickingAt && hoursToPicking >= RISK48_HOURS,
    risk72: hoursToComplete !== null && !completeAt && hoursToComplete >= RISK72_HOURS,
  };
};

export const getOrderItemTotals = (order = {}) => {
  const items = Array.isArray(order.items) ? order.items : [];
  return items.reduce((acc, item) => {
    const qty = Number(item?.qty_ordered) || 0;
    acc.itemCount += 1;
    acc.unitCount += qty;
    return acc;
  }, { itemCount: 0, unitCount: 0 });
};

export const getSpecialCategoryFlags = (order = {}) => {
  const items = Array.isArray(order.items) ? order.items : [];
  return items.reduce((acc, item) => {
    const category = getCategoryInfo(item);
    if (category.key === 'frozen') acc.hasFrozen = true;
    if (category.key === 'wholesale') acc.hasWarehouse = true;
    if (category.key === 'fresh') acc.hasFresh = true;
    return acc;
  }, { hasFrozen: false, hasWarehouse: false, hasFresh: false });
};

export const getOperationalStatus = (order = {}, now = new Date(), pickingStatus = 'picking') => {
  const status = normalizeKey(order.status);
  const assignment = getOrderAssignment(order);
  const sla = calcSla(order, now, pickingStatus);

  if (status === 'processing') return 'before-picking';
  if (status === normalizeKey(pickingStatus)) {
    if (!assignment.currentOperator) return 'picking-unassigned';
    if (sla.risk72 || sla.sla72Breached) return 'picking-at-risk';
    return 'in-picking';
  }
  if (status === 'complete') return 'complete';
  return status || 'unknown';
};

export const buildOrderMetrics = (order = {}, options = {}) => {
  const now = options.now || new Date();
  const pickingStatus = options.pickingStatus || 'picking';
  const assignment = getOrderAssignment(order);
  const sla = calcSla(order, now, pickingStatus);
  const itemTotals = getOrderItemTotals(order);
  const categoryFlags = getSpecialCategoryFlags(order);
  const ageHours = computeAgeHours(order.created_at, now);
  const pickingAgeHours = assignment.assignedAt
    ? (now.getTime() - assignment.assignedAt.getTime()) / (1000 * 60 * 60)
    : null;

  return {
    assignment,
    currentOperator: assignment.currentOperator,
    assignedAt: assignment.assignedAt,
    releasedAt: assignment.releasedAt,
    pickingAgeHours,
    shippingLabel: getFriendlyShipping(order.shipping_description),
    sla,
    ageHours,
    ...itemTotals,
    ...categoryFlags,
    operationalStatus: getOperationalStatus(order, now, pickingStatus),
  };
};
